import { useEffect, useState } from "react";

import { fromSearchResult } from "@/api/schemas";
import type { LibraryShow, SearchResult } from "@/api/schemas";

/**
 * Which show the sheet is showing.
 *
 * Two sources, and the library always wins: a search result is previewed from
 * its own record until the show is tracked, at which point the real library
 * entry takes over without the sheet closing. The open show is looked up on
 * every render rather than stored, so the sheet redraws from the refreshed
 * library after a write instead of from a snapshot taken when it opened.
 */
/**
 * The show id a notification asked for.
 *
 * Read once, from the URL the service worker navigated to, then cleared from
 * the address bar so a reload does not reopen the sheet days later.
 */
function deepLinked(): number | null {
  const raw = new URLSearchParams(globalThis.location.search).get("show");
  if (raw === null) return null;
  const simklId = Number(raw);
  return Number.isInteger(simklId) && simklId > 0 ? simklId : null;
}

export function useSheet(shows: readonly LibraryShow[]) {
  const [openId, setOpenId] = useState<number | null>(deepLinked);

  useEffect(() => {
    if (deepLinked() === null) return;
    globalThis.history.replaceState(null, "", globalThis.location.pathname);
  }, []);
  const [previewed, setPreviewed] = useState<LibraryShow | null>(null);

  const tracked = shows.find((show) => show.show.ids.simkl === openId) ?? null;
  return {
    show: tracked ?? (previewed?.show.ids.simkl === openId ? previewed : null),
    inLibrary: tracked !== null,
    open: setOpenId,
    preview: (result: SearchResult) => {
      setPreviewed(fromSearchResult(result));
      setOpenId(result.ids.simkl);
    },
    close: () => {
      setOpenId(null);
      setPreviewed(null);
    },
  };
}
